import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTriggerDetailsSync, useTriggerLinesSync } from '@/hooks/useSyncMonitoringActions';
import type { OrgProblem, OrgProblemKind } from '@/lib/sync-monitoring';
import { cn } from '@/lib/utils';
import { AlertTriangle, CircleDashed, RefreshCw, XCircle } from 'lucide-react';

const PROBLEM_ICONS: Record<
  OrgProblemKind,
  { Icon: typeof XCircle; className: string }
> = {
  'details-failed': { Icon: XCircle, className: 'text-rose-500' },
  'lines-stale': { Icon: AlertTriangle, className: 'text-amber-500' },
  'never-synced': { Icon: CircleDashed, className: 'text-slate-400' },
};

/**
 * Exception-first list: only the organizations that need attention. Renders
 * nothing when everything is fine (the health banner already says so).
 */
export function SyncProblemsSection({ problems }: { problems: OrgProblem[] }) {
  const triggerDetailsSync = useTriggerDetailsSync();
  const triggerLinesSync = useTriggerLinesSync();

  if (problems.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          À traiter{' '}
          <span className="font-normal text-slate-400">
            ({problems.length})
          </span>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Organisations nécessitant une intervention
        </p>
      </CardHeader>
      <CardContent className="divide-y divide-slate-100">
        {problems.map((problem) => {
          const { Icon, className } = PROBLEM_ICONS[problem.kind];
          const mutation =
            problem.action === 'details' ? triggerDetailsSync : triggerLinesSync;
          const pending =
            mutation.isPending &&
            mutation.variables === problem.org.organizationId;

          return (
            <div
              key={`${problem.org.organizationId}-${problem.kind}`}
              className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', className)} />
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {problem.org.organizationName}
                  </p>
                  <p className="text-xs text-slate-500">{problem.message}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 self-start sm:self-auto"
                disabled={!problem.org.externalOrganizationId || pending}
                onClick={() => mutation.mutate(problem.org.organizationId)}
              >
                <RefreshCw
                  className={cn('mr-1.5 h-3.5 w-3.5', pending && 'animate-spin')}
                />
                {problem.action === 'details'
                  ? 'Relancer orgs & sites'
                  : 'Relancer lignes'}
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
